import { PAGE_LIMIT_MAX } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { guardRoute } from "@/lib/policy/route-guard";
import { DeskTable } from "./-components/desk-table";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";

export const Route = createFileRoute("/_authenticated/_support/support-desk/")({
  validateSearch: (search: Record<string, unknown>): { view?: "resolved" | "all" } => ({
    view: optionalOneOf(["resolved", "all"], search.view),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "platform-console"),
  component: SupportDesk,
});

const VIEWS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
] as const;
type View = (typeof VIEWS)[number]["key"];

function SupportDesk() {
  const { view: viewParam } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const view: View = viewParam ?? "open";
  const setView = (next: View) =>
    void navigate({
      search: { view: next === "open" ? undefined : next },
      ...KEEP_PLACE,
    });
  const list = useQuery(
    orpc.support.requests.list.queryOptions({
      input: { status: view === "all" ? undefined : view, limit: PAGE_LIMIT_MAX, offset: 0 },
    }),
  );
  const rows = list.data?.rows ?? [];

  return (
    <>
      <PageHeader
        title="Support desk"
        count={list.data?.total}
        meta={
          list.data
            ? `${list.data.open} open, every customer's Get help requests, newest first`
            : "Loading…"
        }
      />
      <ListToolbar
        chips={VIEWS.map((option) => (
          <FilterChip
            key={option.key}
            selected={view === option.key}
            count={option.key === "open" ? list.data?.open : undefined}
            onClick={() => setView(option.key)}
          >
            {option.label}
          </FilterChip>
        ))}
      />
      {list.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <ReadFailed
          message="Couldn't load support requests."
          error={list.error}
          onRetry={() => void list.refetch()}
          retrying={list.isRefetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={view === "open" ? "Nothing open" : "No requests"}
          description={
            view === "open"
              ? "Every request has been answered."
              : "Requests customers send from Get help show up here."
          }
        />
      ) : (
        <DeskTable rows={rows} />
      )}
    </>
  );
}
